/**
 * Дерево маршрутов.
 *
 * Пути — из SCREEN_CATALOG §15 «Маршруты и deep links»; там же правило
 * «переход в актуальное состояние» (ПС §20.10): перед показом целевого
 * экрана клиент получает актуальное состояние объекта и показывает
 * закрытое/недоступное состояние вместо несуществующего.
 *
 * Роуты объявлены кодом, а не файлами: дерево небольшое и целиком видно
 * в одном месте — это удобнее при сверке с каталогом.
 */
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Navigate,
  Outlet,
  redirect,
} from '@tanstack/react-router'

import { AppShell } from '../layouts/AppShell'
import { useAppStore } from '../model/app-store'
import { BlockingScreens } from '../screens/system/BlockingScreens'
import { CallHost } from '../screens/calls/CallHost'
import { InAppBanner } from '../screens/notifications/InAppBanner'
import { ShortcutsHost } from '../screens/system/ShortcutsHost'
import { StandaloneWelcomeHost } from '../screens/system/StandaloneWelcome'
import { PushPromptHost } from '../screens/notifications/PushPermission'

import { ChatsScreen } from '../screens/chats/ChatsScreen'
import { AddContactScreen } from '../screens/contacts/AddContactScreen'
import { ContactsScreen } from '../screens/contacts/ContactsScreen'
import { DiscoverySetupScreen } from '../screens/dvizh/DiscoverySetupScreen'
import { LegalScreen } from '../screens/legal/LegalScreen'
import { NotificationTargetScreen } from '../screens/notifications/NotificationTargetScreen'
import { OnboardingScreen } from '../screens/onboarding/OnboardingScreen'
import { DvizhScreen } from '../screens/dvizh/DvizhScreen'
import { PhotosScreen } from '../screens/profile/PhotosScreen'
import { ProfileEditScreen } from '../screens/profile/ProfileEditScreen'
import { ProfilePreviewScreen } from '../screens/profile/ProfilePreviewScreen'
import { ProfileScreen } from '../screens/profile/ProfileScreen'
import { SearchScreen } from '../screens/search/SearchScreen'
import { SettingsScreen } from '../screens/settings/SettingsScreen'
import { SignInScreen } from '../screens/auth/SignInScreen'
import { StartScreen } from '../screens/auth/StartScreen'
import { AppealStatusScreen, ReportStatusScreen } from '../screens/support/StatusScreen'
import { SupportScreen } from '../screens/support/SupportScreen'
import { UserProfileScreen } from '../screens/dvizh/UserProfileScreen'
import { HandleScreen } from '../screens/dvizh/HandleScreen'
import { UsernameScreen } from '../screens/profile/UsernameScreen'

/* ===================================================================== */
/* Корень                                                                 */
/* ===================================================================== */

/**
 * Блокирующие экраны живут ВНУТРИ роутера: они пользуются навигацией
 * («Войти», «Написать в поддержку»), а `useNavigate` вне RouterProvider
 * не работает. Рисуются поверх Outlet, не подменяя маршрут.
 */
const rootRoute = createRootRoute({
  component: () => (
    <>
      <Outlet />
      <BlockingScreens />
      {/* N-06: внутренний баннер о событии другого диалога при видимой вкладке. */}
      <InAppBanner />
      {/* PWA-03: приветствие установленного iPhone Web App — один раз после входа. */}
      <StandaloneWelcomeHost />
      {/* N-03: предложение включить уведомления — поверх любого экрана. */}
      <PushPromptHost />
      {/* D-02: подсказка со списком горячих клавиш. */}
      <ShortcutsHost />
      {/* C-42/C-43 (изменение v1.14): входящий вызов и экран звонка поверх любого экрана. */}
      <CallHost />
    </>
  ),
})

/* ===================================================================== */
/* Публичная часть (до входа)                                             */
/* ===================================================================== */

/** SYS-17: при валидной сессии `/` немедленно уводит в последний раздел. */
function redirectIfAuthenticated(): void {
  const { phase } = useAppStore.getState()
  if (phase === 'ready') throw redirect({ to: '/dvizh' })
  if (phase === 'onboarding') throw redirect({ to: '/onboarding' })
}

const startRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: redirectIfAuthenticated,
  component: StartScreen,
})

const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth',
  beforeLoad: redirectIfAuthenticated,
  component: SignInScreen,
})

const legalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/$document',
  component: LegalScreen,
})

/** A-07 «Не могу войти» доступен без сессии. */
const cantLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/support/cant-login',
  component: () => <SupportScreen anonymous />,
})

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  component: OnboardingScreen,
})

/* ===================================================================== */
/* Авторизованная часть                                                   */
/* ===================================================================== */

/** Гейт: без сессии цель сохраняется и показывается вход (SYS-17). */
function requireSession(): void {
  const store = useAppStore.getState()

  if (store.phase === 'unauthenticated') {
    store.setPendingPath(location.pathname + location.search)
    throw redirect({ to: '/' })
  }

  if (store.phase === 'onboarding') {
    throw redirect({ to: '/onboarding' })
  }
}

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: requireSession,
  component: AppShell,
})

/* --- Движ ---------------------------------------------------------------- */

const dvizhRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dvizh',
  component: DvizhScreen,
})

/**
 * P-13 «Подбор» (изменение v1.12): единственный экран параметров подбора.
 * До него одно и то же лежало в «Фильтрах» над колодой и в «Подборе» профиля.
 */
const discoverySetupRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dvizh/setup',
  component: DiscoverySetupScreen,
})

/** Прежний адрес фильтров (до v1.12) ведёт в единый экран подбора. */
const legacyFiltersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dvizh/filters',
  component: () => <Navigate to="/dvizh/setup" replace />,
})

/** Прежние адреса раздела «Знакомства» (до v1.3) ведут в «Движ». */
const legacyPeopleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/people',
  component: () => <Navigate to="/dvizh" replace />,
})

const legacyPeopleFiltersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/people/filters',
  component: () => <Navigate to="/dvizh/setup" replace />,
})

const userRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/users/$userId',
  validateSearch: (search: Record<string, unknown>): { action?: 'report' | 'block' } => {
    const action = search['action']
    return action === 'report' || action === 'block' ? { action } : {}
  },
  component: UserProfileScreen,
})

/* --- Контакты ---------------------------------------------------------------- */

/**
 * «Контакты» (изменение v1.2): совпадения и прямые диалоги одним алфавитным
 * списком, как в Telegram. Прежний deep link `/matches` (каталог §15) ведёт сюда же.
 */
const contactsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/contacts',
  component: ContactsScreen,
})

/** M-06 «Добавить контакт» по номеру телефона (изменение v1.6). */
const addContactRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/contacts/add',
  component: AddContactScreen,
})

const matchesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/matches',
  component: () => <Navigate to="/contacts" replace />,
})

/** Глобальный поиск по контактам и никнеймам — круглая кнопка рядом с панелью вкладок. */
const searchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/search',
  validateSearch: (search: Record<string, unknown>): { q?: string } =>
    typeof search['q'] === 'string' && search['q'] ? { q: search['q'] } : {},
  component: SearchScreen,
})

/* --- Чаты -------------------------------------------------------------------- */

const chatsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chats',
  component: ChatsScreen,
})

const chatsArchiveRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chats/archive',
  component: () => <ChatsScreen archive />,
})

const chatFoldersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chats/folders',
  component: () => <Navigate to="/settings/folders" replace />,
})

/** Папки настраиваются в разделе «Настройки»; прежняя ссылка сохраняет совместимость. */
const settingsFoldersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/folders',
  component: lazyRouteComponent(() => import('../screens/chats/ChatFoldersScreen'), 'ChatFoldersScreen'),
})

const messageRequestsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chats/requests',
  component: lazyRouteComponent(
    () => import('../screens/chats/MessageRequestsScreen'),
    'MessageRequestsScreen',
  ),
})

/**
 * D-01: на desktop диалог открывается В ДВУХПАНЕЛЬНОМ режиме, поэтому
 * маршрут рендерит ту же страницу «Чаты» — она читает `conversationId`
 * и сама выбирает раскладку по breakpoint'у (§3.5 п. 2–3).
 */
const chatRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chats/$conversationId',
  validateSearch: (search: Record<string, unknown>): { seq?: number } => {
    const seq = Number(search['seq'])
    return Number.isSafeInteger(seq) && seq > 0 ? { seq } : {}
  },
  component: ChatsScreen,
})

const topicChatRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chats/$conversationId/topics/$topicId',
  validateSearch: (search: Record<string, unknown>): { seq?: number } => {
    const seq = Number(search['seq'])
    return Number.isSafeInteger(seq) && seq > 0 ? { seq } : {}
  },
  component: ChatsScreen,
})

const topicPollCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chats/$conversationId/topics/$topicId/poll/new',
  component: lazyRouteComponent(() => import('../screens/chats/PollCreateScreen'), 'PollCreateScreen'),
})

const sharedArchiveRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chats/$conversationId/shared',
  component: lazyRouteComponent(() => import('../screens/chats/SharedArchiveScreen'), 'SharedArchiveScreen'),
})

const mentionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chats/$conversationId/mentions',
  component: lazyRouteComponent(() => import('../screens/chats/MentionsScreen'), 'MentionsScreen'),
})

/** C-36: обсуждение поста канала — отдельный экран и deep link (изменение v1.8). */
const postCommentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chats/$conversationId/posts/$postId',
  validateSearch: (search: Record<string, unknown>): { seq?: number } => {
    const seq = Number(search['seq'])
    return Number.isSafeInteger(seq) && seq > 0 ? { seq } : {}
  },
  component: lazyRouteComponent(() => import('../screens/channels/ChannelRoutes'), 'PostCommentsRoute'),
})

/** C-08: поиск по диалогу — отдельный deep link (§15 каталога). */
const chatSearchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chats/$conversationId/search',
  component: ChatsScreen,
})

const pollCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chats/$conversationId/poll/new',
  component: lazyRouteComponent(() => import('../screens/chats/PollCreateScreen'), 'PollCreateScreen'),
})

/* --- Секретные чаты (изменение v1.13) ------------------------------------------------- */

/** C-38: выбор собеседника для нового секретного чата. */
const secretNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/secret/new',
  component: lazyRouteComponent(() => import('../screens/chats/secret/SecretRoutes'), 'SecretContactsRoute'),
})

/** C-40: экран «Ключ шифрования» — код безопасности и картинка ключа. */
const secretKeyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/chats/$conversationId/key',
  component: lazyRouteComponent(() => import('../screens/chats/secret/SecretRoutes'), 'SecretKeyRoute'),
})

/* --- Уведомления --------------------------------------------------------------- */

/** N-01 — служебный диалог «Движ» в списке чатов: на desktop открывается в правой панели. */
const notificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/notifications',
  component: () => <ChatsScreen notifications />,
})

/** N-05: чтение уведомления и переход к объекту в актуальном состоянии. */
const notificationTargetRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/notifications/$notificationId',
  component: NotificationTargetScreen,
})

/* --- Профиль -------------------------------------------------------------------- */

const meRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/me',
  component: ProfileScreen,
})

const meEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/me/edit',
  component: ProfileEditScreen,
})

/** U-02: предпросмотр публичной проекции профиля. */
const mePreviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/me/preview',
  component: ProfilePreviewScreen,
})

const mePhotosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/me/photos',
  component: PhotosScreen,
})

/** Прежний адрес параметров подбора (до v1.12): подбор переехал во вкладку «Движ». */
const legacyModesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/me/modes',
  component: () => <Navigate to="/dvizh/setup" replace />,
})

/** U-15: никнейм — по нему находят и пишут без совпадения (BS §11.3). */
const meUsernameRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/me/username',
  component: UsernameScreen,
})

/**
 * Deep link `/@никнейм` (каталог §15): один динамический сегмент под корнем приложения.
 * Статические маршруты роутер ранжирует выше, поэтому `/dvizh`, `/chats` и прочие сюда
 * не попадают; путь без `@` уходит в раздел по умолчанию, как любой неизвестный.
 */
/* --- Каналы (изменение v1.7) ------------------------------------------------------ */

/*
 * Экраны каналов грузятся отдельным чанком: ими пользуются реже, чем перепиской, а бюджет
 * §11.1 на чанк маршрута — 120 KB brotli. `lazyRouteComponent` подтягивает модуль при переходе
 * и при `defaultPreload: 'intent'` — заранее, по наведению.
 */
const newChannelRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/channels/new',
  component: lazyRouteComponent(
    () => import('../screens/channels/CreateChannelScreen'),
    'CreateChannelScreen',
  ),
})

/** Группа заводится тем же экраном: разница — только `format` (изменение v1.9). */
const newGroupRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/groups/new',
  component: lazyRouteComponent(() => import('../screens/channels/ChannelRoutes'), 'NewGroupRoute'),
})

const channelRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/channels/$channelId',
  component: lazyRouteComponent(() => import('../screens/channels/ChannelRoutes'), 'ChannelRoute'),
})

const channelEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/channels/$channelId/edit',
  component: lazyRouteComponent(() => import('../screens/channels/ChannelRoutes'), 'ChannelEditRoute'),
})

const channelTypeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/channels/$channelId/type',
  component: lazyRouteComponent(() => import('../screens/channels/ChannelRoutes'), 'ChannelTypeRoute'),
})

const channelSubscribersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/channels/$channelId/subscribers',
  component: lazyRouteComponent(() => import('../screens/channels/ChannelRoutes'), 'ChannelSubscribersRoute'),
})

const channelManagementRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/channels/$channelId/manage/$section',
  component: lazyRouteComponent(
    () => import('../screens/channels/ChannelManagementScreen'),
    'ChannelManagementScreen',
  ),
})

/**
 * Экран ссылки-приглашения частного канала или группы.
 *
 * Сама ссылка остаётся `/+<код>` — как `t.me/+…` в Telegram, — но маршрутом её не описать:
 * роутер не умеет литерал перед параметром внутри сегмента, и путь `/+$inviteCode` не совпадал
 * никогда (проверено `matchPathname`). `/+<код>` попадает в `/$handle`, и `HandleScreen`
 * переводит его сюда (изменение v1.10; до него любое приглашение уводило на «Движ»).
 */
const channelInviteRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/invite/$inviteCode',
  component: lazyRouteComponent(() => import('../screens/channels/ChannelRoutes'), 'ChannelInviteRoute'),
})

const handleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/$handle',
  component: HandleScreen,
})

/* --- Настройки ------------------------------------------------------------------- */

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  component: SettingsScreen,
})

/*
 * Разделы настроек — отдельным чанком: экран большой (все разделы S-02…S-16), а стартовый бандл
 * §11.1 держит слой звонков (изменение v1.14) — входящий вызов не должен зависеть от загрузки
 * чанка. `lazyRouteComponent` переживает сбой загрузки перезагрузкой страницы.
 */
const settingsSectionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/$section',
  component: lazyRouteComponent(
    () => import('../screens/settings/SettingsSectionScreen'),
    'SettingsSectionScreen',
  ),
})

/** SF-04: статус жалобы (GAP-ALL-04 — списка обращений в контракте нет). */
const reportStatusRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports/$reportId',
  component: ReportStatusScreen,
})

/** SF-08: статус апелляции (GAP-ALL-05). */
const appealStatusRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/appeals/$appealId',
  component: AppealStatusScreen,
})

const supportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/support/new',
  component: () => <SupportScreen />,
})

/* ===================================================================== */
/* Сборка                                                                 */
/* ===================================================================== */

const routeTree = rootRoute.addChildren([
  startRoute,
  authRoute,
  legalRoute,
  cantLoginRoute,
  onboardingRoute,
  appRoute.addChildren([
    dvizhRoute,
    legacyPeopleRoute,
    legacyPeopleFiltersRoute,
    discoverySetupRoute,
    legacyFiltersRoute,
    userRoute,
    contactsRoute,
    addContactRoute,
    matchesRoute,
    searchRoute,
    chatsRoute,
    chatsArchiveRoute,
    chatFoldersRoute,
    messageRequestsRoute,
    chatRoute,
    topicChatRoute,
    topicPollCreateRoute,
    chatSearchRoute,
    pollCreateRoute,
    secretNewRoute,
    secretKeyRoute,
    postCommentsRoute,
    notificationsRoute,
    notificationTargetRoute,
    meRoute,
    meEditRoute,
    mePreviewRoute,
    mePhotosRoute,
    legacyModesRoute,
    meUsernameRoute,
    settingsRoute,
    settingsFoldersRoute,
    supportRoute,
    reportStatusRoute,
    appealStatusRoute,
    settingsSectionRoute,
    newChannelRoute,
    newGroupRoute,
    channelRoute,
    channelEditRoute,
    channelTypeRoute,
    channelSubscribersRoute,
    channelManagementRoute,
    sharedArchiveRoute,
    mentionsRoute,
    channelInviteRoute,
    handleRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  // SCREEN_CATALOG §15: неизвестный путь → раздел по умолчанию без ошибки.
  // `throw redirect()` из рендера роутер не перехватывает — это была бы ошибка «Something went wrong».
  defaultNotFoundComponent: () => <Navigate to="/dvizh" replace />,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
